/**
 * Unified status color tokens for gallery review system.
 *
 * Maps review statuses to consistent color tokens across all gallery views.
 * Uses shadcn/ui semantic color names for consistency with the design system.
 *
 * @see /src/components/ui/badge.tsx - Badge component variants
 * @see /src/features/gallery/types/index.ts - PhotoReviewStatus type
 */

import type { PhotoReviewStatus, ReviewStatus } from '@/types/gallery'

/**
 * Status color configuration with all color variants needed for UI.
 * Each status maps to background, text, border, and fill colors.
 */
export const STATUS_COLORS = {
  approved: {
    bg: 'bg-success/10',
    bgSolid: 'bg-success',
    text: 'text-success',
    border: 'border-success/30',
    fill: 'fill-success',
    ring: 'ring-success/20',
  },
  rejected: {
    bg: 'bg-destructive/10',
    bgSolid: 'bg-destructive',
    text: 'text-destructive',
    border: 'border-destructive/30',
    fill: 'fill-destructive',
    ring: 'ring-destructive/20',
  },
  pending: {
    bg: 'bg-warning/10',
    bgSolid: 'bg-warning',
    text: 'text-warning',
    border: 'border-warning/30',
    fill: 'fill-warning',
    ring: 'ring-warning/20',
  },
  under_review: {
    bg: 'bg-info/10',
    bgSolid: 'bg-info',
    text: 'text-info',
    border: 'border-info/30',
    fill: 'fill-info',
    ring: 'ring-info/20',
  },
} as const

export type StatusColorKey = keyof typeof STATUS_COLORS

/**
 * Get color configuration for a given status.
 * Falls back to 'pending' colors for unknown statuses.
 */
export function getStatusColors(status: string | null | undefined) {
  if (!status) return STATUS_COLORS.pending
  return STATUS_COLORS[status as StatusColorKey] ?? STATUS_COLORS.pending
}

/**
 * Get the badge variant for a given status.
 * Maps status strings to shadcn Badge component variants.
 *
 * @example
 * <Badge variant={getStatusBadgeVariant('approved')}>Approved</Badge>
 */
export function getStatusBadgeVariant(
  status: PhotoReviewStatus | ReviewStatus | string | null | undefined
): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline' {
  switch (status) {
    case 'approved':
      return 'success'
    case 'rejected':
      return 'destructive'
    case 'pending':
      return 'warning'
    case 'under_review':
      return 'info'
    default:
      return 'secondary'
  }
}

/**
 * Get human-readable label for a status.
 */
export function getStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'approved':
      return 'Approved'
    case 'rejected':
      return 'Rejected'
    case 'pending':
      return 'Pending'
    case 'under_review':
      return 'Under Review'
    default:
      return 'Unknown'
  }
}

/**
 * Status icon mapping for consistent iconography.
 * Import icons from lucide-react and use this to get the right icon.
 *
 * @example
 * import { CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react'
 * const Icon = STATUS_ICONS[status] // Returns icon component name
 */
export const STATUS_ICON_NAMES = {
  approved: 'CheckCircle',
  rejected: 'XCircle',
  pending: 'Clock',
  under_review: 'AlertTriangle',
} as const

/**
 * Compact status dot for thumbnail indicators.
 * Returns Tailwind classes for a small colored dot.
 */
export function getStatusDotClasses(status: string | null | undefined): string {
  const colors = getStatusColors(status)
  return `w-2 h-2 rounded-full ${colors.bgSolid}`
}
